import ChipsList from 'components/molecules/ChipsList';
import SelectV2 from 'components/atoms/Select';
import {
  SelectDivider,
  SelectWrapper,
} from 'components/molecules/settings/TimeConfigs/styled';
import { InputError } from 'components/atoms/styleds';
import { getDayHourPerHalf, getErrorByKey } from 'utils';

const TimeItem = ({ onSelectTime, item, itemData, error, errors }) => {
  const hours = getDayHourPerHalf();
  return (
    <SelectWrapper>
      <SelectV2
        desktopMb={0}
        mb={0}
        fullWidth={true}
        label='Inizio'
        onChange={onSelectTime}
        name={`time_from-${item.id}`}
        items={hours}
        selectedKey={itemData?.time_from}
        error={getErrorByKey(
          errors,
          `service_opening_hours_attributes.${item.id}.time_from`,
        )}
      />
      <SelectDivider>—</SelectDivider>
      <SelectV2
        desktopMb={0}
        mb={0}
        fullWidth={true}
        label='Fine'
        onChange={onSelectTime}
        name={`time_to-${item.id}`}
        items={hours}
        selectedKey={itemData?.time_to}
        error={getErrorByKey(
          errors,
          `service_opening_hours_attributes.${item.id}.time_to`,
        )}
      />
    </SelectWrapper>
  );
};

const getDayError = (errors, dayIndex) => {
  try {
    if (!errors) {
      return null;
    }

    // Debug logging for day-specific errors

    // Try to get specific day error from service_opening_hours_attributes
    const dayError = getErrorByKey(
      errors,
      `service_opening_hours_attributes.${dayIndex}`,
    );
    if (dayError) {
      // Ensure we return a string
      if (typeof dayError === 'string') {
        return dayError;
      }
      if (Array.isArray(dayError)) {
        return dayError.join(' ');
      }
      return String(dayError);
    }

    // Check for day-specific server format: service_opening_hours.day_0, service_opening_hours.day_1, etc.
    const daySpecificError = errors[`service_opening_hours.day_${dayIndex}`];
    if (daySpecificError) {
      if (typeof daySpecificError === 'string') {
        return daySpecificError;
      }
      if (Array.isArray(daySpecificError)) {
        return daySpecificError.join(' ');
      }
      // Avoid [object Object] display
      if (typeof daySpecificError === 'object' && daySpecificError !== null) {
        return 'Errore negli orari di apertura';
      }
      return String(daySpecificError);
    }

    // Fallback: Check for general server format: service_opening_hours.day_number (for backward compatibility)
    const serverError = errors['service_opening_hours.day_number'];
    if (serverError && typeof serverError === 'string') {
      // Only show general error if no specific day error exists
      return serverError;
    }

    return null;
  } catch (error) {
    console.error('Error in getDayError:', error);
    return 'Errore negli orari';
  }
};

const getDayData = (openingHours = [], dayNumber) => {
  return [...openingHours].find((i) => i.dayNumber === dayNumber && !i.day_off);
};

const getFormattedDays = (openingHours) => {
  const result = {};
  if (openingHours && Array.isArray(openingHours)) {
    openingHours.forEach((i) => {
      // Ensure day_number is a number and within valid range
      const dayNumber = parseInt(i.day_number);
      if (dayNumber >= 0 && dayNumber <= 6) {
        result[dayNumber] = i;
      }
    });
  }

  // Ensure we have all 7 days (0-6)
  for (let i = 0; i < 7; i++) {
    if (!result[i]) {
      result[i] = {
        day_number: i,
        day_off: true,
        time_from: '10:00',
        time_to: '14:00',
      };
    }
  }

  return result;
};

const ServiceWeekTimes = ({ data, onClickChip, onSelectTime, errors }) => {
  const formattedDays = getFormattedDays(data.service_opening_hours_attributes);
  // Create selectedDays based on formatted days to ensure consistency
  const selectedDays = [];
  for (let i = 0; i < 7; i++) {
    const dayData = formattedDays[i];

    if (dayData && !dayData.day_off) {
      selectedDays.push(`${i}-service`);
      console.log(`Day ${i} is selected (not day_off):`, dayData);
    } else {
      console.log(
        `Day ${i} is not selected (day_off: ${dayData?.day_off}):`,
        dayData,
      );
    }
  }

  const weekDays = [
    {
      id: '0-service',
      title: 'Lunedì', // Monday
      additionalContent: !formattedDays[0]?.day_off && (
        <>
          <TimeItem
            onSelectTime={(t) => onSelectTime(t, 0)}
            onSelect={(t) => onSelectTime(t, 0)}
            item={{ id: 0 }}
            itemData={formattedDays[0]}
            errors={errors}
          />
        </>
      ),
      error: getDayError(errors, 0),
    },
    {
      id: '1-service',
      title: 'Martedì', // Tuesday
      additionalContent: !formattedDays[1]?.day_off && (
        <>
          <TimeItem
            onSelectTime={(t) => onSelectTime(t, 1)}
            onSelect={(t) => onSelectTime(t, 1)}
            item={{ id: 1 }}
            itemData={formattedDays[1]}
            errors={errors}
          />
        </>
      ),
      error: getDayError(errors, 1),
    },
    {
      id: '2-service',
      title: 'Mercoledì', // Wednesday
      additionalContent: !formattedDays[2]?.day_off && (
        <>
          <TimeItem
            onSelectTime={(t) => onSelectTime(t, 2)}
            onSelect={(t) => onSelectTime(t, 2)}
            item={{ id: 2 }}
            itemData={formattedDays[2]}
            errors={errors}
          />
        </>
      ),
      error: getDayError(errors, 2),
    },
    {
      id: '3-service',
      title: 'Giovedì', // Thursday
      additionalContent: !formattedDays[3]?.day_off && (
        <>
          <TimeItem
            onSelectTime={(t) => onSelectTime(t, 3)}
            onSelect={(t) => onSelectTime(t, 3)}
            item={{ id: 3 }}
            itemData={formattedDays[3]}
            errors={errors}
          />
        </>
      ),
      error: getDayError(errors, 3),
    },
    {
      id: '4-service',
      title: 'Venerdì', // Friday
      additionalContent: !formattedDays[4]?.day_off && (
        <>
          <TimeItem
            onSelectTime={(t) => onSelectTime(t, 4)}
            onSelect={(t) => onSelectTime(t, 4)}
            item={{ id: 4 }}
            itemData={formattedDays[4]}
            errors={errors}
          />
        </>
      ),
      error: getDayError(errors, 4),
    },
    {
      id: '5-service',
      title: 'Sabato', // Saturday
      additionalContent: !formattedDays[5]?.day_off && (
        <>
          <TimeItem
            onSelectTime={(t) => onSelectTime(t, 5)}
            onSelect={(t) => onSelectTime(t, 5)}
            item={{ id: 5 }}
            itemData={formattedDays[5]}
            errors={errors}
          />
        </>
      ),
      error: getDayError(errors, 5),
    },
    {
      id: '6-service',
      title: 'Domenica', // Sunday
      additionalContent: !formattedDays[6]?.day_off && (
        <>
          <TimeItem
            onSelectTime={(t) => onSelectTime(t, 6)}
            onSelect={(t) => onSelectTime(t, 6)}
            item={{ id: 6 }}
            itemData={formattedDays[6]}
            errors={errors}
          />
        </>
      ),
      error: getDayError(errors, 6),
    },
  ];
  console.log(`[WeekTimes.js::254]:selectedDays`, selectedDays);
  return (
    <div style={{ position: 'relative' }}>
      <ChipsList
        selectedList={selectedDays}
        isColumn={true}
        onlyNative={true}
        onClick={onClickChip}
        list={weekDays}
      />
    </div>
  );
};

export default ServiceWeekTimes;
